/**
 * Created by Rodrigo Quesada on 14/03/17.
 */
import {Component, ViewChild} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {NavigationService} from '../../../../navigation/service/navigation.service';
import {ModalComponent, ModalComponentBase} from '../../../../common/modal/modal-component.class';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {NgForm} from '@angular/forms';
import {FormErrors} from '../../../../components/forms/validation/form-validator.class';
import {FormManager, FormSubmissionStatusCode} from '../../../../components/forms/form-manager.class';
import {Observable} from 'rxjs/Observable';
import {UserAndProfileProjection, UserService} from '../../../../domain/model/user/user.service';
import {User} from '../../../../domain/model/user/user.model';
import {Profile} from '../../../../domain/model/profile/profile.model';
import {UserImpersonationService} from '../../../../domain/model/user/user-impersonation.service';
import {ProfileService} from '../../../../domain/model/profile/profile.service';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {BackendError} from '../../../../common/error/backend-error.class';
import _ = require('lodash');

//region NPI Field Constants
const EXISTING_NPI_ERROR_MESSAGE = 'A profile with the given NPI already exists.';
const NPI_CHANGE_DEBOUNCE_TIME_MS = 1000;
const NPI_REGEX = /^\d{10}$/;

//endregion

//region Other Constants
const EXISTING_USERNAME_ERROR_MESSAGE = 'A user with the given email already exists.';

//endregion

@Component({
    template: require('./add-profile-modal.component.html'),
    styles: [require('./add-profile-modal.component.scss')]
})
export class AddProfileModalComponent extends ModalComponent(ModalComponentBase) {

    //region Form
    private readonly _form$ = new ReplaySubject<NgForm>(1);
    formErrors: FormErrors = {};
    private readonly _formManager: FormManager<User>;

    @ViewChild(NgForm)
    set form(value: NgForm) {
        this._form$.next(value);
    }

    //endregion

    //region NPI Field
    readonly npiMask = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];
    private readonly _specificValidationMessages = {
        npi: 'The NPI should be 10 digits long.'
    };

    readonly npiChange$ = new ReplaySubject<string>(1);
    private readonly _npiError$ = new BehaviorSubject<string|undefined>(undefined);
    //endregion

    model: UserAndProfileProjection = {user: new User(), profile: new Profile()};
    error: string;
    readonly saving$ = new BehaviorSubject(false);
    impersonateUserAfterCreation: boolean = false;

    closeButtonDisabledValue$: Observable<string|null>;
    saveButtonDisabledValue$: Observable<string|null>;

    constructor(navigationService: NavigationService,
                readonly modal: NgbActiveModal,
                fmgm: FormManager.InjectedMembers,
                userService: UserService,
                private readonly _profileService: ProfileService,
                private readonly _impersonationService: UserImpersonationService) {
        super(navigationService);

        this._formManager = new FormManager<User>(
            fmgm,
            this._form$,
            Observable.of(() => userService.createUser(this.model)),
            Observable.of(this._specificValidationMessages),
            false
        );
        combineLatest(this._formManager.displayedErrors$, this._npiError$,
            (errors, npiError) => {
                if (npiError) return _.merge({}, errors, {npi: npiError});
                else return errors;
            })
            .subscribe(errors => this.formErrors = errors);

        this.saveButtonDisabledValue$ = Observable.combineLatest(
            this._formManager.hasErrors$,
            this._npiError$,
            this.saving$,
            (hasErrors, npiError, saving) => hasErrors || npiError || saving ? '' : null
        );
        this.closeButtonDisabledValue$ = this.saving$.map(it => it ? '' : null);

        this._initNpiFields();
    }

    save() {
        if (!this.saving$.value) {
            this.saving$.next(true);
            this._formManager.save()
                .finally(() => this.saving$.next(false))
                .subscribe(result => {
                    if (result.status.code === FormSubmissionStatusCode.SUBMITTED) {
                        if (this.impersonateUserAfterCreation) {
                            const user = result.data!;
                            this._impersonationService.impersonateUser(user, ['personal-details']);
                        }
                        else this.modal.close();
                    }
                    else if (result.status.code === FormSubmissionStatusCode.ERROR) {
                        const error: Partial<BackendError> = result.error || {};
                        switch (error.name) {
                            case 'ExistingNpiError':
                                this.error = EXISTING_NPI_ERROR_MESSAGE;
                                break;
                            case 'ExistingUsernameError':
                                this.error = EXISTING_USERNAME_ERROR_MESSAGE;
                                break;
                            default:
                                this.error = 'We are sorry, a server error just occurred.';
                        }
                    }
                });
        }
    }

    onSubmit() {
        this.save();
    }

    //region NPI Field
    private _initNpiFields() {
        this.npiChange$.debounceTime(NPI_CHANGE_DEBOUNCE_TIME_MS)
            .distinctUntilChanged()
            .switchMap(value => {
                if (NPI_REGEX.test(value)) return this._profileService.doesProfileExistFor(value);
                else return Observable.of(false);
            })
            .subscribe(exists => {
                let error: string|undefined;
                if (exists) {
                    error = EXISTING_NPI_ERROR_MESSAGE;
                }
                this._npiError$.next(error);
            });
    }

    //endregion
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/admin/search/modals/add-profile-modal.component.ts